import { httpClient } from '@connectors/HttpClient'

import type { Subscription, School, User, Invitee } from '../types'

export type CreateSchoolParams = Partial<School>

export type AddUserBody = {
    email: string
    role: string
}

export type InviteUserBody = {
    email: string
    role: string
    schoolSubscriptionIds: string[]
}

export type UpdateUserBody = {
    role?: string
    schoolSubscriptionIds?: string[]
    notifyOnSeatAddition: boolean
}

type InviteUserOptions = {
    queueOnly: boolean
}

export type AddSubscriptionParams = Partial<Omit<Subscription, 'startDate' | 'endDate'>> & {
    productIds: string[]
    startDate: string
    endDate: string
}

export type AssignedSchoolUers = {
    add: { [schoolSubscriptionId: string]: string[] }
    remove: { [schoolSubscriptionId: string]: string[] }
}

export type SearchSchoolResponseSchoolSubscription = {
    claim: {
        id: string
    }[]
    seatCount: number
    subscription: {
        name: string
    }
    _count: { schoolSubscriptionSeats: number }
}

export type SearchSchoolsResponse = {
    schools: (School & {
        schoolSubscriptions: SearchSchoolResponseSchoolSubscription[]
        _count: { schoolSubscriptions: number; schoolUsers: number }
    })[]
    totalCount: number
}

export const SchoolService = {
    search: async ({
        offset,
        limit,
        searchValue = '',
        sortBy = 'createdAt',
        sortOrder = 'desc'
    }: {
        offset: number
        limit: number
        searchValue?: string
        sortBy?: string
        sortOrder?: string
    }): Promise<SearchSchoolsResponse> => {
        const response = await httpClient.axios.get('/admin/schools', {
            params: {
                offset,
                limit,
                searchValue,
                sortBy,
                sortOrder
            }
        })
        return response.data
    },
    fineSearch: async ({
        offset,
        limit,
        postalCode,
        country,
        name,
        domain,
        govId,
        sortBy = 'createdAt',
        sortOrder = 'desc'
    }: {
        offset: number
        limit: number
        postalCode?: string
        country?: string
        name?: string
        domain?: string
        sortBy?: string
        govId?: string
        sortOrder?: string
    }): Promise<{
        totalCount: number
        schools: School[]
    }> => {
        const response = await httpClient.axios.get('/admin/schools/fine-search', {
            params: {
                offset,
                limit,
                postalCode,
                country,
                name,
                domain,
                govId,
                sortBy,
                sortOrder
            }
        })
        return response.data
    },
    create: async (params: CreateSchoolParams): Promise<School> => {
        const response = await httpClient.axios.post('/admin/schools', params)
        return response.data
    },
    get: async (id: string): Promise<School> => {
        const response = await httpClient.axios.get(`/admin/schools/${id}`)
        return response.data
    },
    update: async (id: string, params: CreateSchoolParams): Promise<any> => {
        const response = await httpClient.axios.put(`/admin/schools/${id}`, params)
        return response.data
    },
    remove: async (id: string): Promise<any> => {
        const response = await httpClient.axios.delete(`/admin/schools/${id}`)
        return response.data
    },
    updateUser: async (schoolId: string, userId: string, body: UpdateUserBody): Promise<any> => {
        const response = await httpClient.axios.put(`/admin/schools/${schoolId}/users/${userId}`, body)
        return response.data
    },
    listSubscriptions: async (schoolId: string): Promise<Subscription[]> => {
        const response = await httpClient.axios.get(`/admin/schools/${schoolId}/subscriptions`)
        return response.data
    },
    getSubscription: async (schoolId: string, subscriptionId: string): Promise<Subscription> => {
        const response = await httpClient.axios.get(`/admin/schools/${schoolId}/subscriptions/${subscriptionId}`)
        return response.data
    },
    addSubscription: async (schoolId: string, params: AddSubscriptionParams): Promise<Subscription> => {
        const response = await httpClient.axios.post(`/admin/schools/${schoolId}/subscriptions`, params)
        return response.data
    },
    updateSubscription: async (id: string, subscriptionId: string, params: AddSubscriptionParams): Promise<any> => {
        const response = await httpClient.axios.put(`/admin/schools/${id}/subscriptions/${subscriptionId}`, params)
        return response.data
    },
    cancelSubscription: async (id: string, subscriptionId: string, issueRefund: boolean): Promise<any> => {
        const response = await httpClient.axios.put(`/admin/schools/${id}/subscriptions/${subscriptionId}/cancel`, {
            issueRefund
        })
        return response.data
    },
    deleteSubscription: async (id: string, subscriptionId: string): Promise<any> => {
        const response = await httpClient.axios.delete(`/admin/schools/${id}/subscriptions/${subscriptionId}`)
        return response.data
    },
    assignUsers: async (id: string, params: AssignedSchoolUers): Promise<any> => {
        const response = await httpClient.axios.post(`/admin/schools/${id}/subscriptions/assign-users`, params)
        return response.data
    },
    removeUser: async (id: string, userId: string): Promise<any> => {
        const response = await httpClient.axios.delete(`/admin/schools/${id}/users/${userId}`)
        return response.data
    },
    getInvited: async (id: string): Promise<Invitee[]> => {
        const response = await httpClient.axios.get(`/admin/schools/${id}/invitees`)
        return response.data
    },
    invite: async (schoolId: string, body: InviteUserBody, options?: InviteUserOptions): Promise<User> => {
        const params = new URLSearchParams()
        const queueOnly = options && options.queueOnly ? 'true' : 'false'

        params.append('queueOnly', queueOnly)

        const response = await httpClient.axios.post(`/admin/schools/${schoolId}/invitees?${params.toString()}`, body)
        return response.data
    },
    removeInvite: async (id: string, inviteeId: string): Promise<any> => {
        const response = await httpClient.axios.delete(`/admin/schools/${id}/invitees/${inviteeId}`)
        return response.data
    },
    searchInvite: async (schoolId: string, email: string): Promise<Invitee> => {
        const params = new URLSearchParams()

        params.append('email', email.toLowerCase().trim())

        const response = await httpClient.axios.get(`/admin/schools/${schoolId}/invitees/search?${params.toString()}`)
        return response.data
    },
    updateSubscriptionSeats: async (
        id: string,
        subscriptionId: string,
        {
            seatCount,
            billable,
            skipNotification = false
        }: {
            seatCount: number
            billable: boolean
            skipNotification?: boolean
        }
    ): Promise<any> => {
        const response = await httpClient.axios.put(`/admin/schools/${id}/subscriptions/${subscriptionId}/seats`, {
            seatCount,
            billable,
            skipNotification
        })
        return response.data
    },
    getSubscriptionBillableActions: async (id: string, subscriptionId: string): Promise<any> => {
        const response = await httpClient.axios.get(
            `/admin/schools/${id}/subscriptions/${subscriptionId}/billable-actions`
        )
        return response.data
    }
}
